import { useEffect, useState } from 'react';
import { FormikValues } from 'formik';

import {
  adminActions,
  adminActivePharmacyList,
  getPublishedSoftwareList,
  IAdminPharmacyListBaseData,
  MiscType,
  resetStatus,
  successSelector,
  userIdSelector,
} from '@pharmaplan/common';

import { IDynamicTableObject } from '../../../components/DynamicTable/types';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';

import { useAppDispatch } from '../../useAppDispatch';
import useFormikUtils from '../../useFormik/useFormikUtils';
import { useComponentDidUpdate } from '../../useComponentDidUpdate';
import useFormik from '../../useFormik/useFormik';
import { useAppSelector } from '../../useAppSelector';
import strings from '../../../localization';
import { PharmacyListTabTypes } from '../../../helpers/Constants';

import usePharmacyListTabs from './usePharmacyListTabs';
import usePharmacyOwnership from './usePharmacyOwnership';
import useTableSelectCheckbox from '../../useTableSelectCheckbox';
import useTableSort from '../../useTableSort';
import useAdmin from '../../useAdmin';

const initialValues = {
  banners: [],
  userText: '',
  contactName: '',
  city: '',
  softwareId: '',
  ownerIds: [],
};

const usePharmacyList = () => {
  const { setPage, page, urlSelectedTab } = useFormikUtils(
    Object.keys(PharmacyListTabTypes),
    PharmacyListTabTypes.myAccounts,
  );

  const [selectedTab, setSelectedTab] = useState<PharmacyListTabTypes>(
    urlSelectedTab as PharmacyListTabTypes,
  );

  const dispatch = useAppDispatch();

  const { isSuperAdmin } = useAdmin();
  const { isDesc, orderBy, handleSorting, resetSorting, order } = useTableSort();

  const isActiveTab = selectedTab === PharmacyListTabTypes.activePharmacies;
  const isMyAccounts = selectedTab === PharmacyListTabTypes.myAccounts;
  const canBulkAssign = isSuperAdmin && isActiveTab;

  const currentUserId = useAppSelector(userIdSelector);

  const ownershipParam = (ownerIds: Array<string> = []) => {
    if (isMyAccounts) {
      return [currentUserId];
    }
    return ownerIds;
  };

  const {
    showAssignOwner,
    ownershipSuccess,
    acknowledgeOwnershipChange,
  } = usePharmacyOwnership();

  // Selection is only ever offered on the active tab, so its list drives the
  // "all selected" state. Read here because the row data below needs `selection`.
  const activeList = useAppSelector(adminActivePharmacyList);

  const {
    selectAll,
    selectedIds,
    handlePress,
    selectAllFunc,
    allIds,
    handleReset: resetSelection,
  } = useTableSelectCheckbox({
    page,
    useOfEdit: false,
    userType: MiscType.All,
    dataLength: activeList?.data?.length ?? 0,
  });

  const selection = canBulkAssign ? { selectedIds, handlePress } : null;

  const { headers, rows, api, data, loadSuccess } = usePharmacyListTabs(
    selectedTab,
    selection,
  );
  const { totalCount, data: rowData } = data ?? {};

  const handleSelectAll = selectAllFunc({
    data: (rowData ?? []) as Array<IAdminPharmacyListBaseData>,
    idProperty: 'pharmacyId',
  });

  const hasSelection = allIds.length > 0;

  const bulkAssignOwner = () => {
    showAssignOwner(allIds)();
  };

  const deactivatedSuccess = useAppSelector((state) =>
    successSelector([adminActions.disapprove], state));

  const activatedSuccess = useAppSelector((state) =>
    successSelector([adminActions.approve], state));

  const handleSubmit = (values: FormikValues, inPage = 1) => {
    const { softwareId, banners, userText, contactName, city, ownerIds } = values ?? {};
    setPage(inPage);
    resetSelection();

    dispatch(
      api({
        page: inPage,
        softwareId,
        banners,
        userText: userText?.trim(),
        sortBy: orderBy,
        desc: isDesc,
        contactName: contactName?.trim(),
        city: city?.trim(),
        ownerIds: ownershipParam(ownerIds),
      }),
    );
  };

  const { formik, clearURL } = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    page,
    selectedTab,
  });

  const { values } = formik ?? {};

  const handlePagination = (_: unknown, selectedPage: number) => {
    const gotoPage = selectedPage + 1;
    setPage(gotoPage);
    dispatch(
      api({
        page: gotoPage,
        sortBy: orderBy,
        desc: isDesc,
        ...values,
        ownerIds: ownershipParam(values?.ownerIds),
      }),
    );
  };

  const refreshList = () => {
    dispatch(
      api({
        page,
        sortBy: orderBy,
        desc: isDesc,
        ...values,
        ownerIds: ownershipParam(values?.ownerIds),
      }),
    );
  };

  const handleReset = () => {
    clearURL();
    formik.resetForm();
    setPage(1);
    resetSorting();
    resetSelection();
    dispatch(api({ page: 1, ownerIds: ownershipParam() }));
  };

  const table: IDynamicTableObject = {
    headerBar: [],
    headers,
    rows,
  };

  useEffect(() => {
    dispatch(getPublishedSoftwareList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useComponentDidUpdate(() => {
    handleReset();
    resetSorting();
  }, [selectedTab]);

  useEffect(() => {
    if (activatedSuccess) {
      dispatch(resetStatus([adminActions.approve]));
      dispatch(api({ page: 1, ownerIds: ownershipParam() }));
      showSuccess(dispatch, strings.userActivatedSuccessfully);
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activatedSuccess]);

  useEffect(() => {
    if (deactivatedSuccess) {
      dispatch(resetStatus([adminActions.disapprove]));
      dispatch(api({ page: 1, ownerIds: ownershipParam() }));
      showSuccess(dispatch, strings.deactivatedSuccessfully);
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deactivatedSuccess]);

  useEffect(() => {
    if (ownershipSuccess) {
      acknowledgeOwnershipChange();
      resetSelection();
      refreshList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownershipSuccess]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      dispatch(
        api({
          page: 1,
          sortBy: orderBy,
          desc: isDesc,
          ...values,
          ownerIds: ownershipParam(values?.ownerIds),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return {
    table,
    formik,
    handlePagination,
    page,
    setSelectedTab,
    selectedTab,
    handleReset,
    totalCount,
    rowData,
    loadSuccess,
    handleSorting,
    order,
    orderBy,
    canBulkAssign,
    selectAll,
    handleSelectAll,
    bulkAssignOwner,
    hasSelection,
    isMyAccounts,
  };
};

export default usePharmacyList;
